"use client";

import { displayLabel } from "@/lib/display";
import { CheckIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { SessionRecord } from "@/lib/api";
import {
  formatDuration,
  formatTime,
  maskPhone,
  parseIntentDirections,
  parseTags,
  scoreBgColor,
  scorePercent,
  scoreTextColor,
  SOURCE_LABELS,
  DIM_LABELS,
} from "@/lib/utils";

const EVAL_DIMS = [
  "slot_completion",
  "compliance",
  "naturalness",
  "objection_handling",
  "goal_achievement",
  "topic_convergence",
] as const;

type EvalDim = (typeof EVAL_DIMS)[number];

const END_STATE_COLORS: Record<string, string> = {
  completed: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  amd_detected: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  failed: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  no_answer: "bg-neutral-100 text-neutral-800 dark:bg-neutral-900 dark:text-neutral-200",
};

function dimScoreKey(dim: EvalDim): `${EvalDim}_score` {
  return `${dim}_score`;
}

function outcomeVariant(reason: string | null | undefined): "default" | "secondary" | "destructive" | "outline" {
  if (!reason) return "outline";
  if (reason === "success") return "default";
  return "destructive";
}

function EvalBlocks({ session }: { session: SessionRecord }) {
  const scores = EVAL_DIMS.map((d) => ({
    key: d,
    label: DIM_LABELS[d] ?? d,
    score: session[dimScoreKey(d)] as number | null | undefined,
  }));
  const hasAny = scores.some((x) => x.score != null);
  if (!hasAny) return <span className="text-muted-foreground">-</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex gap-0.5">
          {scores.map((x) => (
            <div key={x.key} className={`h-4 w-1.5 rounded-sm ${scoreBgColor(x.score)}`} />
          ))}
        </div>
      </TooltipTrigger>
      <TooltipContent side="left" className="text-xs">
        {scores.map((x) => (
          <div key={x.key} className="flex justify-between gap-4">
            <span>{x.label}</span>
            <span className="tabular-nums">{x.score != null ? x.score.toFixed(2) : "-"}</span>
          </div>
        ))}
      </TooltipContent>
    </Tooltip>
  );
}

export interface SessionColumn {
  key: string;
  header: React.ReactNode;
  align?: "left" | "right";
  render: (s: SessionRecord, ctx: { onOpenRow: (id: string) => void }) => React.ReactNode;
}

export const SESSION_COLUMNS = {
  time: {
    key: "time",
    header: "时间",
    render: (s, { onOpenRow }) => (
      <button
        type="button"
        onClick={() => onOpenRow(s.session_id)}
        className="hover:underline"
      >
        {formatTime(s.created_at)}
      </button>
    ),
  },
  source: {
    key: "source",
    header: "来源",
    render: (s) => <span title={SOURCE_LABELS[s.source] ?? s.source}>{SOURCE_LABELS[s.source] ?? s.source}</span>,
  },
  phone: {
    key: "phone",
    header: "电话",
    render: (s) => (
      <span className="font-mono text-xs tabular-nums">{maskPhone(s.phone)}</span>
    ),
  },
  number: {
    key: "number",
    header: "号码",
    render: (s, { onOpenRow }) => (
      <button
        type="button"
        onClick={() => onOpenRow(s.session_id)}
        className="font-mono text-xs hover:underline"
      >
        {s.phone ?? ""}
      </button>
    ),
  },
  playbook: {
    key: "playbook",
    header: "话术",
    render: (s) => <span className="font-mono text-xs">{s.playbook_id}</span>,
  },
  turns: {
    key: "turns",
    header: "轮数",
    align: "right",
    render: (s) => s.turn_count,
  },
  duration: {
    key: "duration",
    header: "时长",
    align: "right",
    render: (s) => formatDuration(s.duration_ms),
  },
  tags: {
    key: "tags",
    header: "标签",
    render: (s) => (
      <div className="flex flex-wrap gap-1">
        {parseTags(s.tags).map((tag) => (
          <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
        ))}
      </div>
    ),
  },
  audited: {
    key: "audited",
    header: "✓",
    render: (s) =>
      s.audited_at ? <CheckIcon className="size-3.5 text-muted-foreground" /> : null,
  },
  status: {
    key: "status",
    header: "状态",
    render: (s) =>
      s.ended_at ? (
        <Badge variant="secondary">已结束</Badge>
      ) : (
        <Badge variant="outline">进行中</Badge>
      ),
  },
  outcome: {
    key: "outcome",
    header: "对话结果",
    render: (s) => (
      <>
        {s.answered === false ? (
          <Badge variant="destructive" className="text-xs">未接听</Badge>
        ) : s.end_reason ? (
          <Badge variant={outcomeVariant(s.end_reason)} className="text-xs">
            {displayLabel(s.end_reason)}
          </Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
        {s.answered !== false && s.hangup_by && (
          <div className="mt-0.5 text-[10px] text-muted-foreground">
            {s.hangup_by === "nord" ? "AI 挂断" : "对方挂断"}
          </div>
        )}
      </>
    ),
  },
  callState: {
    key: "callState",
    header: "对话状态",
    render: (s) =>
      s.call_end_state ? (
        <span
          className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${
            END_STATE_COLORS[s.call_end_state] ?? "bg-muted text-muted-foreground"
          }`}
        >
          {displayLabel(s.call_end_state)}
        </span>
      ) : (
        <span className="text-muted-foreground">-</span>
      ),
  },
  overall: {
    key: "overall",
    header: "综合评分",
    align: "right",
    render: (s) => (
      <span className={`tabular-nums ${scoreTextColor(s.eval_score)}`}>
        {scorePercent(s.eval_score)}
      </span>
    ),
  },
  goal: {
    key: "goal",
    header: "目标达成",
    align: "right",
    render: (s) => (
      <span className={`tabular-nums ${scoreTextColor(s.goal_achievement_score)}`}>
        {scorePercent(s.goal_achievement_score)}
      </span>
    ),
  },
  intent: {
    key: "intent",
    header: "意向",
    align: "right",
    render: (s) => (
      <span className={`tabular-nums ${scoreTextColor(s.intent_score)}`}>
        {scorePercent(s.intent_score)}
      </span>
    ),
  },
  intentDirections: {
    key: "intentDirections",
    header: "意向方向",
    render: (s) => {
      const dirs = parseIntentDirections(s.intent_directions);
      if (dirs.length === 0) return <span className="text-muted-foreground">-</span>;
      return (
        <div className="flex flex-wrap gap-1">
          {dirs.map((d) => (
            <Badge key={d} variant="outline" className="text-xs">{d}</Badge>
          ))}
        </div>
      );
    },
  },
  evalBar: {
    key: "evalBar",
    header: "评估",
    render: (s) => <EvalBlocks session={s} />,
  },
  amd: {
    key: "amd",
    header: "语音信箱",
    render: (s) =>
      s.amd_detected === 1 ? (
        <Badge variant="outline" className="bg-amber-100 text-amber-800 text-xs">
          语音信箱
        </Badge>
      ) : null,
  },
} satisfies Record<string, SessionColumn>;

export type SessionColumnKey = keyof typeof SESSION_COLUMNS;
